// ============================================================================
// The printed tag contract — ONE definition of the string on the label.
//
// This is the highest-consequence constant in the fleet module: it is stamped
// onto physical labels by a vendor and cannot be changed afterwards. The QR
// generator, the route table and the resolver all read it from here so they
// cannot drift.
//
// WHY IT IS ALL UPPERCASE. QR alphanumeric mode packs two characters into 11
// bits, but its charset is `0-9 A-Z space $ % * + - . / :` — no lowercase. URL
// scheme and host are case-insensitive per RFC 3986 and we control the path, so
// an all-caps payload encodes in alphanumeric rather than byte mode and drops a
// whole QR version for free (v3/29x29 instead of v4/33x33 for this shape). The
// hyphen in FM-0001 is in the charset, so the URL matches the id printed in
// large type beside the code — which is what makes a damaged tag recoverable by
// typing.
//
// WHY THE ID IS IN THE URL rather than an opaque token: the tag already prints
// FM-0007 in 9pt, so a token hides nothing from anyone holding it; a scratched
// code can still be typed; and a replacement tag needs no token-mapping table
// that must never be lost.
//
// Ids are self-typing (FM- / VEH- / IM-), which is why there is no type segment
// and why one printed URL shape serves every asset class.
// ============================================================================

/** The host the tags are printed against. Frozen at print time — see the
 *  module header. Currently the production app host, so no new DNS is needed;
 *  a dedicated qr. subdomain can be added later and point at the same path
 *  without reprinting anything, which is the entire reason for the shim. */
export const TAG_HOST = 'APP.TERMINALCLEANING.COM';

/** The stable printed path. Re-pointing where it sends people is a code
 *  change; changing THIS is a reprint of every label.
 *
 *  2026-08-27: Ben ruled the payload for the 100-tag print run as
 *  `HTTPS://APP.TERMINALCLEANING.COM/A/FM-0001`, so the printed path is now
 *  `A`. It was `T` from the 2026-08-17 build (PR 301) and NO tag has been
 *  printed against either yet — the tag-size decision is still open — but see
 *  LEGACY_TAG_PATHS: a path that has ever been printable is never retired. */
export const TAG_PATH = 'A';

/** Every path shape the app must keep answering FOREVER, in addition to
 *  TAG_PATH. A tag that 404s once teaches the field to stop scanning and the
 *  only remedy is a reprint, so a retired path stays wired at a cost of two
 *  route lines. `T` shipped in PR 301 and is kept for that reason alone. */
export const LEGACY_TAG_PATHS = ['T'] as const;

/** Every URL path the tag route is registered at, upper and lower case for
 *  each. The printed payload is uppercase, but a native camera hands the URL to
 *  the default browser verbatim and a person typing the id off a scratched
 *  label will type lowercase — so `/A/FM-0001`, `/a/fm-0001` and `/A/fm-0001`
 *  must all land on the same asset. The id itself is normalised in
 *  parseAssetId; this list is the path half of the same guarantee. Built here
 *  rather than written into the router so the two cannot drift. */
export const TAG_ROUTE_PATHS: string[] = [TAG_PATH, ...LEGACY_TAG_PATHS].flatMap((p) => [
  p.toUpperCase(),
  p.toLowerCase(),
]);

export const ASSET_ID_RE = /^(FM|VEH|IM)-\d{4,5}$/;

export type AssetPrefix = 'FM' | 'VEH' | 'IM';

/** Detail route per prefix. Changing a value here re-points every printed tag
 *  of that class; that is the indirection working as intended. */
export const ROUTE_BY_PREFIX: Record<string, string> = {
  FM: '/fleet/equipment',
  VEH: '/fleet/vehicle',
};

/** The exact string encoded into a QR code. Uppercase, alphanumeric-safe. */
export function tagUrl(assetId: string): string {
  return `HTTPS://${TAG_HOST}/${TAG_PATH}/${assetId.trim().toUpperCase()}`;
}

export interface ParsedAssetId {
  /** Normalised, uppercase. */
  id: string;
  prefix: AssetPrefix | null;
  /** Shape only — never a lookup. A lookup here is what would leak. */
  wellFormed: boolean;
  /** Where to send a signed-in scanner, or null if we do not route this class. */
  route: string | null;
}

export function parseAssetId(raw: string | undefined | null): ParsedAssetId {
  const id = (raw ?? '').trim().toUpperCase();
  const wellFormed = ASSET_ID_RE.test(id);
  const prefix = wellFormed ? (id.split('-')[0] as AssetPrefix) : null;
  const base = prefix ? ROUTE_BY_PREFIX[prefix] : undefined;
  return {
    id,
    prefix,
    wellFormed,
    route: wellFormed && base ? `${base}/${id}` : null,
  };
}
